import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { getSessions } from '../services/api';

interface Session {
  sessionId: string;
  language: string;
  status: string;
  location?: {
    state: string;
    district: string;
    village: string;
    gps?: { lat: number; lng: number };
  };
  enumeratorName: string;
  supervisorName?: string;
  createdAt: string;
}

export default function MapPage() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['sessions-map'],
    // gpsOnly → backend returns a minimal projection (no 500/5000 cap) for
    // every session that has coordinates, so the map shows them all.
    queryFn: () => getSessions({ gpsOnly: 'true' }),
  });

  const sessions: Session[] = (data?.sessions ?? []).filter(
    (s: Session) => s.location?.gps?.lat && s.location?.gps?.lng,
  );

  const statusColor: Record<string, string> = {
    completed: '#10b981',
    uploading: '#3b82f6',
    failed: '#ef4444',
    draft: '#9ca3af',
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Map View</h1>
        <p className="text-gray-500 text-sm mt-1">
          {isLoading ? 'Loading…' : `${sessions.length} sessions with GPS coordinates`}
        </p>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-sm">
        {Object.entries(statusColor).map(([status, color]) => (
          <div key={status} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-gray-600 capitalize">{status}</span>
          </div>
        ))}
      </div>

      {/* Map */}
      <div className="flex-1 min-h-[500px] rounded-xl overflow-hidden border border-gray-200 shadow-sm">
        <MapContainer
          center={[20.5937, 78.9629]}   // Center of India
          zoom={5}
          style={{ height: '100%', width: '100%' }}
          preferCanvas   // render CircleMarkers on a canvas — stays smooth with thousands of points
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {sessions.map((session) => (
            <CircleMarker
              key={session.sessionId}
              center={[session.location!.gps!.lat, session.location!.gps!.lng]}
              radius={6}
              pathOptions={{
                fillColor: statusColor[session.status] ?? '#9ca3af',
                fillOpacity: 0.8,
                color: '#fff',
                weight: 1.5,
              }}
            >
              <Popup>
                <div className="text-sm space-y-1 min-w-[180px]">
                  <p className="font-semibold">{session.language}</p>
                  <p className="text-gray-600">
                    {session.location?.village}, {session.location?.district}
                  </p>
                  <p className="text-gray-600">{session.location?.state}</p>
                  <p className="text-gray-500 text-xs">
                    Supervisor: {session.supervisorName || '—'}
                  </p>
                  <div className="pt-1 border-t border-gray-100">
                    <button
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                      className="text-brand-600 hover:text-brand-800 text-xs font-medium"
                    >
                      View Session →
                    </button>
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
